/**
 * iter641 — Setup Wizard.
 * A 5-step guided flow for new branches:
 *   1. Logo upload (tenant-wide thermal/PDF logo)
 *   2. Receipt header/footer text + invoice title
 *   3. Printer setup hand-off (link to /printer-setup)
 *   4. First test print (push test endpoint)
 *   5. Done!
 *
 * Designed to be linear: each step's "Next" only enables once the
 * minimum requirement is met OR the user clicks "Skip for now". The
 * wizard remembers its position in localStorage so a refresh doesn't
 * lose progress.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { CheckCircle2, ChevronRight, ChevronLeft, Upload, Receipt as ReceiptIcon, Printer, Sparkles, ImageIcon } from 'lucide-react';
import api from '@/lib/api';
import { useBranchStore } from '@/stores';

const STORAGE_KEY = 'smarto-setup-wizard-step';

const STEPS = [
  { id: 1, key: 'logo', title: 'Upload your logo', icon: ImageIcon },
  { id: 2, key: 'receipt', title: 'Receipt header & footer', icon: ReceiptIcon },
  { id: 3, key: 'printer', title: 'Connect a printer', icon: Printer },
  { id: 4, key: 'test', title: 'Print a test ticket', icon: Sparkles },
  { id: 5, key: 'done', title: 'All set!', icon: CheckCircle2 },
];

export default function SetupWizardPage() {
  const navigate = useNavigate();
  const branches = useBranchStore(s => s.branches);
  const [step, setStep] = useState(() => {
    try { return Math.max(1, Math.min(5, parseInt(localStorage.getItem(STORAGE_KEY)) || 1)); }
    catch { return 1; }
  });
  const [branding, setBranding] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [branchId, setBranchId] = useState('');
  const [header, setHeader] = useState('');
  const [footer, setFooter] = useState('Thank you!');
  const [savingReceipt, setSavingReceipt] = useState(false);
  const [printers, setPrinters] = useState([]);
  const [testing, setTesting] = useState('');

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, String(step)); } catch { /* noop */ }
  }, [step]);

  useEffect(() => {
    api.get('/branding').then(r => setBranding(r.data || {})).catch(() => setBranding({}));
    api.get('/cashier/printers').then(r => setPrinters(r.data || [])).catch(() => setPrinters([]));
  }, []);

  useEffect(() => {
    if (!branchId && (branches || []).length > 0) setBranchId(branches[0].id);
  }, [branches, branchId]);

  useEffect(() => {
    if (!branchId) return;
    const b = (branches || []).find(x => x.id === branchId);
    if (b) {
      setHeader(b.receipt_header || '');
      setFooter(b.receipt_footer || 'Thank you!');
    }
  }, [branchId, branches]);

  const handleLogoUpload = async (file) => {
    if (!file) return;
    if (file.size > 2_000_000) {
      toast.error('Logo must be under 2 MB');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      const { data } = await api.post('/branding/upload-logo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBranding(prev => ({ ...(prev || {}), logo_url: data.logo_url || data.url }));
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Logo upload failed');
    } finally {
      setUploading(false);
    }
  };

  const saveReceipt = async () => {
    if (!branchId) { toast.error('Pick a branch first'); return; }
    setSavingReceipt(true);
    try {
      await api.put(`/branches/${branchId}/receipt-settings`, {
        receipt_header: header.trim(),
        receipt_footer: footer.trim() || 'Thank you!',
      });
      toast.success('Receipt text saved');
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
      return false;
    } finally {
      setSavingReceipt(false);
    }
  };

  const runTestPrint = async (printerId) => {
    setTesting(printerId);
    try {
      await api.post(`/cashier/printers/${printerId}/test`);
      toast.success('Test ticket sent — check the printer');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test print failed');
    } finally {
      setTesting('');
    }
  };

  const canAdvance = useMemo(() => {
    if (step === 1) return Boolean(branding?.logo_url);
    if (step === 2) return Boolean(branchId);
    if (step === 3) return printers.length > 0;
    return true;
  }, [step, branding, branchId, printers]);

  const nextLabel = step === 5 ? 'Open POS' : 'Next';
  const goNext = async () => {
    if (step === 2) {
      const ok = await saveReceipt();
      if (!ok) return;
    }
    if (step === 5) {
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
      navigate('/cashier');
      return;
    }
    setStep(s => Math.min(5, s + 1));
  };

  const current = STEPS[step - 1];

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5" data-testid="setup-wizard-page">
        <header>
          <h1 className="text-2xl sm:text-3xl font-bold font-outfit">Setup Wizard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Five quick steps to get your first branch ready to print real receipts.
          </p>
        </header>

        {/* Step indicator */}
        <div className="flex items-center gap-2" data-testid="wizard-stepper">
          {STEPS.map((s, idx) => {
            const Icon = s.icon;
            const done = step > s.id;
            const active = step === s.id;
            return (
              <div key={s.id} className="flex items-center gap-2 flex-1">
                <div
                  className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 transition border ${
                    done ? 'bg-emerald-500 text-white border-emerald-500'
                        : active ? 'bg-primary text-primary-foreground border-primary shadow-md shadow-primary/30'
                                 : 'bg-stone-100 text-stone-400 border-stone-200 dark:bg-stone-800 dark:border-stone-700'
                  }`}
                  data-testid={`wizard-step-dot-${s.id}`}
                >
                  {done ? <CheckCircle2 size={16} /> : <Icon size={15} />}
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 transition ${done ? 'bg-emerald-500' : 'bg-stone-200 dark:bg-stone-700'}`} />
                )}
              </div>
            );
          })}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base font-outfit flex items-center gap-2" data-testid="wizard-step-title">
              <span className="text-xs font-bold text-primary tracking-wide uppercase">Step {step}/5</span>
              <span>·</span>
              <span>{current.title}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {step === 1 && (
              <div className="space-y-3" data-testid="wizard-step-logo">
                <p className="text-sm text-muted-foreground">
                  Used on PDF invoices, thermal receipts and the customer-facing portal. PNG or JPG, square works best.
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24 rounded-xl border border-dashed border-stone-300 dark:border-stone-700 bg-stone-50 dark:bg-stone-900 flex items-center justify-center overflow-hidden">
                    {branding?.logo_url ? (
                      <img src={branding.logo_url} alt="Brand logo" className="w-full h-full object-contain" data-testid="wizard-logo-preview" />
                    ) : (
                      <ImageIcon size={28} className="text-stone-400" />
                    )}
                  </div>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={(e) => handleLogoUpload(e.target.files?.[0])}
                      data-testid="wizard-logo-input"
                    />
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg border border-input bg-background hover:bg-stone-50 dark:hover:bg-stone-800 transition">
                      <Upload size={14} /> {uploading ? 'Uploading…' : branding?.logo_url ? 'Replace logo' : 'Choose file'}
                    </span>
                  </label>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-3" data-testid="wizard-step-receipt">
                <p className="text-sm text-muted-foreground">
                  Lines printed at the top and bottom of every thermal receipt at this branch.
                </p>
                <div>
                  <Label className="text-xs">Branch</Label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    value={branchId}
                    onChange={(e) => setBranchId(e.target.value)}
                    data-testid="wizard-branch-select"
                  >
                    {(branches || []).map(b => (<option key={b.id} value={b.id}>{b.name}</option>))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Header (max 200 chars)</Label>
                  <Textarea
                    rows={2}
                    maxLength={200}
                    placeholder="VAT 30012345 · 123 Main St · 0500-000-000"
                    value={header}
                    onChange={(e) => setHeader(e.target.value)}
                    data-testid="wizard-receipt-header"
                  />
                </div>
                <div>
                  <Label className="text-xs">Footer</Label>
                  <Input
                    maxLength={200}
                    placeholder="Thank you!"
                    value={footer}
                    onChange={(e) => setFooter(e.target.value)}
                    data-testid="wizard-receipt-footer"
                  />
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-3" data-testid="wizard-step-printer">
                <p className="text-sm text-muted-foreground">
                  Need a printer? The Print Bridge runs on a PC/Mac on the same network as your iPad &mdash;
                  download it from <a className="text-primary underline" href="/printer-setup" target="_blank" rel="noreferrer">/printer-setup</a>.
                </p>
                <div className="rounded-lg border bg-stone-50 dark:bg-stone-900 p-3 text-sm">
                  {printers.length > 0
                    ? <div data-testid="wizard-printer-list">
                        <span className="font-semibold text-emerald-700">{printers.length}</span> printer{printers.length === 1 ? '' : 's'} registered:
                        <ul className="mt-1.5 space-y-0.5">
                          {printers.slice(0, 5).map(p => (
                            <li key={p.id} className="text-xs text-muted-foreground">· {p.name} ({p.type}{p.station ? ` · ${p.station}` : ''})</li>
                          ))}
                        </ul>
                      </div>
                    : <span className="text-muted-foreground">No printers yet — open Printer Setup to add one.</span>}
                </div>
                <Button variant="outline" onClick={() => window.open('/printer-setup', '_blank')} data-testid="wizard-open-printer-setup">
                  <Printer size={14} className="mr-1.5" /> Open Printer Setup
                </Button>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-3" data-testid="wizard-step-test">
                <p className="text-sm text-muted-foreground">Send a one-line test ticket to confirm everything's wired up.</p>
                {printers.length === 0 ? (
                  <div className="text-sm text-stone-500">No printers found — back up to step 3.</div>
                ) : (
                  <div className="space-y-2">
                    {printers.map(p => (
                      <div key={p.id} className="flex items-center justify-between border rounded-lg px-3 py-2" data-testid={`wizard-test-row-${p.id}`}>
                        <div>
                          <div className="text-sm font-medium">{p.name}</div>
                          <div className="text-[11px] text-muted-foreground">{p.type}{p.station ? ` · ${p.station}` : ''} · {p.ip_address || 'no IP'}</div>
                        </div>
                        <Button size="sm" onClick={() => runTestPrint(p.id)} disabled={testing === p.id} data-testid={`wizard-test-btn-${p.id}`}>
                          {testing === p.id ? 'Sending…' : 'Send test'}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {step === 5 && (
              <div className="text-center py-6 space-y-3" data-testid="wizard-step-done">
                <div className="w-20 h-20 rounded-full bg-emerald-100 dark:bg-emerald-900/30 mx-auto flex items-center justify-center">
                  <CheckCircle2 size={42} className="text-emerald-600" />
                </div>
                <h2 className="text-xl font-bold font-outfit">You&apos;re ready to sell!</h2>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  Open the POS to start taking orders. You can revisit Manage Hub anytime to fine-tune categories, taxes, payments and more.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer nav */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => setStep(s => Math.max(1, s - 1))}
            disabled={step === 1}
            data-testid="wizard-back-btn"
          >
            <ChevronLeft size={14} className="mr-1" /> Back
          </Button>
          <div className="flex items-center gap-2">
            {step < 5 && (
              <Button variant="outline" onClick={() => setStep(s => Math.min(5, s + 1))} data-testid="wizard-skip-btn">
                Skip for now
              </Button>
            )}
            <Button
              onClick={goNext}
              disabled={(step !== 5 && !canAdvance) || savingReceipt}
              data-testid="wizard-next-btn"
              className="min-w-[120px]"
            >
              {savingReceipt ? 'Saving…' : nextLabel} {step < 5 && <ChevronRight size={14} className="ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
